import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
  nofollow?: boolean;
  canonical?: string;
  alternateLanguages?: { [key: string]: string };
  siteName?: string;
  locale?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image = '/logo.svg',
  url,
  type = 'website',
  author = 'Winnomi AI',
  publishedTime,
  modifiedTime,
  jsonLd,
  noindex = false,
  nofollow = false,
  canonical,
  alternateLanguages,
  siteName = 'Winnomi AI',
  locale,
}) => {
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const { t, i18n } = useTranslation();
  const currentLang = lang || i18n.language || 'tr';
  
  // Default values based on language
  const defaultTitle = currentLang === 'en' 
    ? 'Winnomi AI - AI-Powered Social Media Analytics Platform'
    : 'Winnomi AI - AI Destekli Sosyal Medya Analitik Platformu';
  const defaultDescription = currentLang === 'en'
    ? 'Grow your business with AI-powered social media analytics platform. Instagram, Facebook, Twitter, LinkedIn and TikTok analytics in one platform.'
    : 'AI destekli sosyal medya analitik platformu ile işletmenizi büyütün. Instagram, Facebook, Twitter, LinkedIn ve TikTok analitiği tek platformda.';
  const defaultKeywords = currentLang === 'en'
    ? 'social media analytics, instagram analytics, facebook analytics, twitter analytics, linkedin analytics, tiktok analytics, AI analysis, social media management, sosyal medya marketing, social media reporting'
    : 'sosyal medya analitik, instagram analitik, facebook analitik, twitter analitik, linkedin analitik, tiktok analitik, AI analiz, sosyal medya yönetimi, sosyal medya pazarlama, sosyal medya raporlama';
  const defaultLocale = currentLang === 'en' ? 'en_US' : 'tr_TR';
  
  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalLocale = locale || defaultLocale;
  
  const baseUrl = 'https://winnomi.com';
  const currentUrl = url || `${baseUrl}${location.pathname}`;
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  const canonicalUrl = canonical || currentUrl;
  
  // Generate alternate languages
  const pathWithoutLang = location.pathname.replace(/^\/(tr|en)/, '') || '/home';
  const defaultAlternateLanguages = {
    'tr': `${baseUrl}/tr${pathWithoutLang}`,
    'en': `${baseUrl}/en${pathWithoutLang}`,
    'x-default': `${baseUrl}/tr${pathWithoutLang}`
  };
  const finalAlternateLanguages = alternateLanguages || defaultAlternateLanguages;

  // Default JSON-LD for Organization
  const defaultJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Winnomi AI',
    url: baseUrl,
    logo: `${baseUrl}/logo.svg`,
    description: currentLang === 'en' 
      ? 'AI-powered social media analytics platform'
      : 'AI destekli sosyal medya analitik platformu',
    sameAs: [
      // sosyal medya hesaplarınızı buraya ekleyebilirsiniz
      // 'https://twitter.com/winnomi',
      // 'https://www.linkedin.com/company/winnomi',
      // 'https://www.facebook.com/winnomi',
      // 'https://www.instagram.com/winnomi',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'info@winnomi.com',
    },
  };

  // Combine default and custom JSON-LD
  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const allJsonLd = [defaultJsonLd, ...jsonLdArray];

  // Robots meta
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
  ].join(', ');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={finalLocale} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Alternate Languages */}
      {finalAlternateLanguages &&
        Object.entries(finalAlternateLanguages).map(([langCode, href]) => (
          <link key={langCode} rel="alternate" hrefLang={langCode} href={href} />
        ))}

      {/* JSON-LD Structured Data */}
      {allJsonLd.map((ld, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
        />
      ))}
    </Helmet>
  );
};

export default SEO;

